import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Payload } from '@kehe/phoenix-generic-utils';
import { Observable, throwError } from 'rxjs';
import { TransactionType } from '../models/transaction-type.model';
import { environment } from '@src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { ApiFailureText } from '@app/enum/alert-text.enum';
import { SpendCategoryResponseDto } from '@app/modules/supplier-deductions/ar-billings/models/ar-billings-page-table-response';

@Injectable({ providedIn: 'root' })
export class TransactionTypeService {
  httpClient = inject(HttpClient);

  getTransactionTypes(): Observable<Payload<TransactionType[]>> {
    const url = `${environment.arBillingsApi}/transaction-type/list`;

    return this.httpClient
      .get<Payload<TransactionType[]>>(url, {})
      .pipe(
        catchError(() => throwError(() => new Error(ApiFailureText.Generic))),
      );
  }

  reactivateTransactionTypeByCode(code: string): Observable<void> {
    const url = `${environment.arBillingsApi}/transaction-type/reactivate-code`;

    return this.httpClient
      .post<void>(url, { code })
      .pipe(
        catchError(() => throwError(() => new Error(ApiFailureText.Generic))),
      );
  }

  deactivateTransactionTypeByCode(code: string): Observable<void> {
    const url = `${environment.arBillingsApi}/transaction-type/deactivate-code`;

    return this.httpClient
      .post<void>(url, { code })
      .pipe(
        catchError(() => throwError(() => new Error(ApiFailureText.Generic))),
      );
  }

  getSpendCategoryList() {
    const spendCategoryUrl = `${environment.workdayIntegrationApi}/dependencies/spendcategories`;

    return this.httpClient
      .get<SpendCategoryResponseDto[]>(spendCategoryUrl)
      .pipe(
        map((response) =>
          response.map((r) => ({
            value: `${r.spendCategoryName} (${r.referenceId})`,
            codeValue: r.referenceId,
          })),
        ),
      );
  }

  saveTransactionType(payload): Observable<any> {
    const url = `${environment.arBillingsApi}/transaction-type`;
    const formData = new FormData();
    formData.append('TransactionCode', payload.invoicePrefix);
    formData.append('LineIdentifier', payload.lineIdentifier);
    formData.append('Name', payload.transactionTypeDescription);
    if (payload.id) {
      formData.append('Id', payload.id);
    }
    formData.append(
      'InvoiceHeaderDescription',
      payload.invoiceHeaderDescription,
    );
    formData.append('SpendCategoryId', payload.spendCategoryId);
    if (payload.isHandKey !== undefined) {
      formData.append('IsHandKey', payload.isHandKey);
    }
    if (!payload.isHandKey) {
      formData.append('TemplateFile', payload.file.rawFile, payload.file.name);
      formData.append('CostCenter', payload.costCenter);
      formData.append('IsGroupedByEsn', payload.invoiceType);
      formData.append('Chain', payload.chain);
      formData.append('IncludeBrokerData', 'true');
      formData.append('HasSupportingDocument', payload.hasSupportingDocument);
      if (payload.supportingDocumentFile) {
        formData.append(
          'SupportingDocumentFile',
          payload.supportingDocumentFile.rawFile,
          payload.supportingDocumentFile.name,
        );
      }
    }

    formData.append('HasEpFee', payload.hasEpFee);

    return this.httpClient.post<any>(`${url}`, formData);
  }

  getPreSignedTemplateURL(
    type: string,
    isEsnTemplate: boolean = false,
  ): Observable<string> {
    const url = `${environment.arBillingsApi}/jobfile/template-url/${type}?isEsnBaseTemplate=${isEsnTemplate}`;

    return this.httpClient
      .get(url, { responseType: 'text' })
      .pipe(
        catchError(() => throwError(() => new Error(ApiFailureText.Generic))),
      );
  }

  getPreSignedSupportingDocumentURL(type: string): Observable<string> {
    const url = `${environment.arBillingsApi}/jobfile/supporting-document-url/${type}`;

    return this.httpClient
      .get(url, { responseType: 'text' })
      .pipe(
        catchError(() => throwError(() => new Error(ApiFailureText.Generic))),
      );
  }
}
